// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import {
  addCouponApi,
  getCouponsApi,
  updateCouponApi,
  deleteCouponApi
} from "../../../../services/Web/coupon.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

// ** Async Thunks
export const fetchCoupons = createAsyncThunk(
  "coupon/fetchAll",
  async (query = "", { rejectWithValue }) => {
    try {
      const response = await getCouponsApi(query)
      
      return response.data // ✔ return full response
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch coupons"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const createCoupon = createAsyncThunk(
  "coupon/create",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addCouponApi(formData)
      toastSuccess("Coupon created successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to create coupon"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const updateCoupon = createAsyncThunk(
  "coupon/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateCouponApi(id, formData)
      toastSuccess("Coupon updated successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update coupon"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const deleteCoupon = createAsyncThunk(
  "coupon/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteCouponApi(id)
      toastSuccess("Coupon deleted successfully")
      return { id, ...response.data }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete coupon"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

// ** Initial State
const initialState = {
  coupons: [],
  selectedCoupon: null,
  loading: false,
  error: null,
  total: 0,
  lastUpdated: null
}

// ** Slice
export const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    clearSelectedCoupon: (state) => {
      state.selectedCoupon = null
    },
    clearError: (state) => {
      state.error = null
    },
    resetCouponState: (state) => {
      state.coupons = []
      state.selectedCoupon = null
      state.loading = false
      state.error = null
      state.total = 0
      state.lastUpdated = null
    },
    updateCouponFormData: (state, action) => {
      const updatedData = action.payload

      if (state.selectedCoupon) {
        state.selectedCoupon = {
          ...state.selectedCoupon,
          ...updatedData
        }
      }

      // Also update in the coupons array if it exists there
      if (state.selectedCoupon && state.selectedCoupon._id) {
        const couponIndex = state.coupons.findIndex(
          (coupon) => coupon._id === state.selectedCoupon._id
        )
        if (couponIndex !== -1) {
          state.coupons[couponIndex] = {
            ...state.coupons[couponIndex],
            ...updatedData
          }
        }
      }

      state.lastUpdated = new Date().toISOString()
    },
    setSelectedCoupon: (state, action) => {
      state.selectedCoupon = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all coupons
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false

        const payload = action.payload || {} // payload = response.data

        state.coupons = Array.isArray(payload.coupons) ? payload.coupons : []

        state.total = payload.total || state.coupons.length
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })

      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      // Create coupon
      .addCase(createCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.loading = false
        const newCoupon = action.payload.data || action.payload

        state.coupons.push(newCoupon)
        state.total += 1
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      // Update coupon
      .addCase(updateCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.loading = false
        const updatedCoupon = action.payload.data || action.payload
        const couponIndex = state.coupons.findIndex(
          (coupon) => coupon._id === updatedCoupon._id
        )

        if (couponIndex !== -1) {
          state.coupons[couponIndex] = updatedCoupon
        }

        if (
          state.selectedCoupon &&
          state.selectedCoupon._id === updatedCoupon._id
        ) {
          state.selectedCoupon = updatedCoupon
        }

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      // Delete coupon
      .addCase(deleteCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.loading = false
        const deletedId = action.payload.id || action.payload.data?.id

        state.coupons = state.coupons.filter(
          (coupon) => coupon._id !== deletedId
        )
        state.total -= 1

        if (state.selectedCoupon && state.selectedCoupon._id === deletedId) {
          state.selectedCoupon = null
        }

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
  }
})

// ** Actions
export const {
  clearSelectedCoupon,
  clearError,
  resetCouponState,
  updateCouponFormData,
  setSelectedCoupon
} = couponSlice.actions

// ** Selectors
export const selectCoupons = (state) => state.coupon.coupons
export const selectSelectedCoupon = (state) => state.coupon.selectedCoupon
export const selectCouponLoading = (state) => state.coupon.loading
export const selectCouponError = (state) => state.coupon.error
export const selectCouponTotal = (state) => state.coupon.total
export const selectCouponLastUpdated = (state) => state.coupon.lastUpdated

// ** Reducer
export default couponSlice.reducer
